import React, { useState, useCallback, useEffect } from "react";
import ContentWrapper from "../views/components/ContentWrapper";
import { deleteDocument, getDocuments, registerFile, updateDocument, uploadFile } from "../data/dataApi";
import { Form, Button } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { formatDate } from "../views/helpers";
import { useUserState } from "../views/components/UserStateProvider";
import EditBtn from "../images/edit.svg";
import DeleteBtn from "../images/delete.svg";
import PaperClipWhite from "../images/paper-clip-white.svg";
import File from "../views/components/File";
import ConfirmationModal from "../views/components/ConfirmationModal";
import { ProgressBar } from "../views/components/ProgressBar";
import { useParams } from "react-router-dom";
import Pagination from "../views/components/Pagination";
import DocumentForm from "../views/components/DocumentForm";
import { isValidUrl } from "../views/helpers";

const Documents = () => {
    const { userRole } = useUserState();

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadedDocs, setUploadedDocs] = useState([]);
    const [uploadStarted, setUploadStarted] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadFailed, setUploadFailed] = useState(false);
    const [resetFileQueue, setResetFileQueue] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteFileError, setDeleteFileError] = useState(false);
    const [fileId, setFileId] = useState();
    const initialPdfUrl = [{
        file: {
            path: "",
        },
        source_url: "",
        title: "",
    }];
    const [pdfUrls, setPdfUrls] = useState(initialPdfUrl);
    const [uploadError, setUploadError] = useState();
    const initialEditingModeItem = {
        index: null,
        id: "",
        title: "",
        source_url: "",
    };
    const [editingModeItem, setEditingModeItem] = useState(initialEditingModeItem);
    const [showEditSuccessModal, setShowEditSuccessModal] = useState(false);
    const [editError, setEditError] = useState(false);
    const [saving, setSaving] = useState(false);

    const { page } = useParams();

    const listLimit = 20;

    const onDrop = useCallback(async acceptedFiles => {
        if (acceptedFiles.length > 0) {
            const files = acceptedFiles.map(file => {
                return {
                    file,
                    source_url: "",
                    title: "",
                }
            });

            if (resetFileQueue) {
                setSelectedFiles([...files]);
                setResetFileQueue(false);
            } else {
                setSelectedFiles(prevState => [...prevState, ...files]);
            }
        }
    }, [resetFileQueue]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled: uploadStarted,
        accept: {
            'text/plain': ['.txt'],
            'text/csv': ['.csv'],
            'application/pdf': ['.pdf'],
        }
    });

    const handleNewInfo = async () => {
        setUploadFailed(false);
        setUploadError("");

        if(selectedFiles.length) {
            for(let i = 0; i < selectedFiles.length; i++) {
                if(!selectedFiles[i].source_url || !isValidUrl(selectedFiles[i].source_url)) {
                    setUploadError("Please make sure all source URL(s) are set and valid.");
                    return;
                } else if(!selectedFiles[i].title) {
                    setUploadError("Please input a title for all of the documents.");
                    return;
                }
            }
        }

        if(pdfUrls[0].source_url || (!pdfUrls[0].source_url && pdfUrls.length > 1)) {
            for (let i = 0; i < pdfUrls.length; i++) {
                if (!pdfUrls[i].source_url || (!isValidUrl(pdfUrls[i].source_url) || !pdfUrls[i].source_url.endsWith(".pdf"))) {
                    setUploadError("Please make sure all PDF URL(s) are valid.");
                    return;
                }
            }
        }

        setUploadStarted(true);
        const response = await uploadFile(selectedFiles, pdfUrls[0] ? pdfUrls : [], setUploadProgress, setUploadFailed);

        if (response.success) {
            const { filesData } = response;

            const registerFilesResponse = await registerFile(filesData, setUploadProgress, setUploadFailed);

            if(registerFilesResponse.success) {
                registerFilesResponse.data.forEach(doc => {
                    setUploadedDocs(prevState => [{
                        filename: doc.filename,
                        source_url: doc.source_url,
                        title: doc.title,
                        file_id: doc.file_id,
                        uploaded_by: doc.uploaded_by,
                        upload_date: doc.upload_date,
                        status: doc.status,
                    }, ...prevState]);
                });
                setUploadStarted(false);
                setResetFileQueue(true);
            } else {
                setUploadFailed(true);
            }
        } else {
            setUploadFailed(true);
        }
    };

    const handleTitleEdit = (e, index) => {
        const { value } = e.target;

        setUploadedDocs(prevState => {
            let newState = [...prevState];
            newState[index] = {
                ...newState[index],
                title: value,
            };
            return newState;
        });
    };

    const handleSaveFile = async () => {
        setEditError("");

        if(!uploadedDocs[editingModeItem.index].title) {
            setEditError("Please input a document title.");
        } else if(!isValidUrl(uploadedDocs[editingModeItem.index].source_url)) {
            setEditError("Please make sure the source URL is valid.");
        } else if(!editError) {
            setSaving(true);
            const response = await updateDocument(editingModeItem.file_id, uploadedDocs[editingModeItem.index].title, uploadedDocs[editingModeItem.index].source_url);
            
            if(response.success) {
                setEditingModeItem(initialEditingModeItem);
                setShowEditSuccessModal(true);
            } else {
                setEditError(response.error.message);
            }

            setSaving(false);
        }
    };

    const handleDeleteClick = id => {
        setFileId(id);
        setShowDeleteModal(true);
    };

    const handleDocDelete = async () => {
        setDeleteFileError("");

        const response = await deleteDocument(fileId);

        if (response.success) {
            setUploadedDocs(prevState => prevState.filter(doc => doc.id !== fileId));
            setEditingModeItem(initialEditingModeItem);
        } else {
            setDeleteFileError(response.error.message);
        }

        return response.success;
    };

    const handlePdfUrl = (type, val, index) => {
        if(uploadProgress === 100) setUploadProgress(0);

        setPdfUrls(prevState => {
            let newState = [...prevState];

            if(type === "source-url") {
                newState[index] = {
                    ...newState[index],
                    file: {
                        path: val.split("/").pop(),
                    },
                    source_url: val,
                };
            } else if(type === "title") {
                newState[index] = {
                    ...newState[index],
                    title: val,
                };
            }

            return newState;
        });
    };
    
    const handleRemovePdfUrl = index => {
        setPdfUrls(prevState => {
            let newState = [...prevState];

            return newState.filter((_, i) => i !== index);
        });
    };

    const getStatusLabel = status => {
        if(status === "in_progress") {
            return "In Progress";
        } else if(status === "completed") {
            return "Completed";
        } else if(status === "error") {
            return "Error";
        }
    };

    useEffect(() => {
        (async () => {
            const response = await getDocuments(page === undefined ? 0 : page - 1);

            if (response.success) {
                setUploadedDocs(response.data.rows);
            }
        })();
    }, [page]);

    useEffect(() => {
        setUploadProgress(0);
    }, [selectedFiles]);

    useEffect(() => {
        if(uploadProgress === 100) {
            setPdfUrls(initialPdfUrl);
        }
    }, [uploadProgress]);

    return (
        <ContentWrapper className="documents scrollable">
            <div className="wrapper">
                <div className="file-upload-wrapper tif-mb-20">
                    <p className="tif-mb-15">Upload files here:</p>
                    <div id="file-upload" className="tif-bg-color-romeo tif-py-30 tif-color-golf text-center tif-mb-25" {...getRootProps()}>
                        <input {...getInputProps()} />
                        {
                            isDragActive ?
                                <p>Drop the files here</p>
                                :
                                <p>Drag and drop files here, or click to select files</p>
                        }
                    </div>
                    {
                        selectedFiles.map((_, index) => (
                            <File key={index} files={selectedFiles} position={index} uploadProgress={uploadProgress} onUpdate={setSelectedFiles} uploadStarted={uploadStarted} failed={uploadFailed} />
                        ))
                    }
                    <Form className="secondary-form tif-mb-40">
                        <h4 className="tif-pl-25 tif-mb-20">Document URL</h4>
                        <div className="card bg-transparent overflow-hidden">
                            {
                                pdfUrls.map((url, index) => (
                                    <div className={`tif-py-20 tif-pr-70 tif-pl-25 ${ index % 2 === 0 ? 'tif-bg-color-quarternary' : 'tif-bg-color-bravo' }`} key={index}>
                                        <DocumentForm name="url-file" onChange={ handlePdfUrl } file={ url } position={ index } uploadStarted={ uploadStarted } uploadProgress={ uploadProgress } />
                                        {
                                            pdfUrls.length > 1 &&
                                            <Button className="dashboard-senary-btn tif-mt-15 tif-mr-40" onClick={ () => handleRemovePdfUrl(index) }>Remove</Button>
                                        }
                                        {
                                            index === pdfUrls.length - 1 &&
                                            <Button className="dashboard-senary-btn tif-mt-15" onClick={() => setPdfUrls(prevState => [...prevState, initialPdfUrl])}>+ Add Document</Button>
                                        }
                                    </div>
                                ))
                            }
                        </div>
                    </Form>
                    <ProgressBar show={uploadStarted || uploadProgress === 100} progress={uploadProgress} failed={uploadFailed} />
                    {
                        (selectedFiles.length || (pdfUrls.length && pdfUrls[0].source_url)) ? <Button className="dashboard-quarternary-btn" onClick={handleNewInfo} disabled={uploadProgress === 100 && !uploadFailed}>{uploadFailed && "Retry "}Upload</Button> : <></>
                    }
                    {uploadError && <p className="error-text">{uploadError}</p>}
                </div>

                {
                    uploadedDocs.map((doc, docIndex) => (
                        <div className="card tif-pt-15 tif-pb-10 tif-pl-30 tif-pr-85 tif-bg-light-color-papa tif-mb-30 tif-color-white tif-light-color-november tif-bg-color-bravo" key={docIndex}>
                            <p className="tif-font-sm tif-mb-5">{formatDate(doc.upload_date)}</p>
                            <p className="tif-font-sm tif-mb-20">By {doc.uploaded_by}</p>
                            
                            {
                                editingModeItem.index !== docIndex ?
                                    <p className="message tif-mb-20"><img src={PaperClipWhite} className="tif-mr-md-10 tif-mr-5" alt="Paper clip icon" />{doc.filename}</p>
                                :
                                    <Form className="secondary-form">
                                        <Form.Group className="form-group tif-mb-20" controlId={`formEditTitle_${ docIndex }`}>
                                            <Form.Label className="tif-mb-15">Title</Form.Label>
                                            <Form.Control
                                                placeholder="Type here"
                                                className="small tif-mb-15"
                                                value={doc.title}
                                                onChange={(e) => handleTitleEdit(e, docIndex)}
                                            />
                                        </Form.Group>

                                        <Form.Group className="form-group" controlId={`formEditSourceUrl_${ docIndex }`}>
                                            <Form.Label className="tif-mb-15">Source URL</Form.Label>
                                            <Form.Control
                                                placeholder="Type here"
                                                value={doc.source_url}
                                                onChange={e => setUploadedDocs(prevState => {
                                                    let newState = [...prevState];
                                                    newState[docIndex] = {
                                                        ...newState[docIndex],
                                                        source_url: e.target.value,
                                                    };

                                                    return newState;
                                                })}
                                                className="small"
                                            />
                                        </Form.Group>

                                        <Button className="dashboard-quarternary-btn tif-mt-35" onClick={ handleSaveFile } disabled={ saving }>{ saving ? "Saving..." : "Save" }</Button>
                                        <Button className="dashboard-septenary-btn tif-mt-35 tif-ml-20" onClick={() => setEditingModeItem(initialEditingModeItem)} disabled={ saving }>Cancel</Button>
                                        
                                        { editError && <p className="error-text">{ editError }</p> }
                                    </Form>
                            }

                            <p className="tif-font-sm tif-mb-0">Status: <span className={`status-pill ${doc.status !== "in_progress" ? doc.status : ""}`}>{ getStatusLabel(doc.status) }</span></p>
                            
                            <div className="edit-bot">
                                {
                                    (userRole === "admin" && doc.status !== "in_progress") &&
                                    <>
                                        <Button className="edit-btn tif-mb-15" onClick={() => setEditingModeItem({ index: docIndex, title: doc.title, source_url: doc.source_url, id: doc.id })} disabled={ editingModeItem.index === docIndex }>
                                            <img src={EditBtn} alt="Edit icon" />
                                        </Button>
                                        <Button className="edit-btn" onClick={() => handleDeleteClick(doc.id)}>
                                            <img src={DeleteBtn} alt="Delete icon" />
                                        </Button>
                                    </>
                                }
                            </div>
                        </div>
                    ))
                }

                <Pagination listLength={ uploadedDocs.length && uploadedDocs[0].total_count } path="/dashboard/documents" page={ page } limit={ listLimit } />
            </div>
            <ConfirmationModal
                show={showDeleteModal}
                changeVisibility={setShowDeleteModal}
                type="delete"
                confirmationButton="Delete File"
                confirmationMessage="Are you sure you want to delete this file?"
                successMessage="This file has been successfully deleted."
                modalFunction={handleDocDelete}
                close={
                    <Button className="dashboard-tertiary-btn" onClick={() => setShowDeleteModal(false)}>
                        Back to Documents
                    </Button>
                }
                error={deleteFileError}
            />
            <ConfirmationModal
                show={ showEditSuccessModal}
                changeVisibility={ setShowEditSuccessModal }
                type="success"
                successMessage="You have successfully updated the document metadata."
                close={
                    <Button className="dashboard-tertiary-btn" onClick={() => setShowEditSuccessModal(false)}>
                        Back to Documents
                    </Button>
                } />
        </ContentWrapper>
    );
};

export default Documents;